import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";

const GooglePayButton = ({
  amount,
  paymentGuid,
  onPaymentSuccess,
  onPaymentError,
  disabled = false,
}) => {
  const [isMonerisReady, setIsMonerisReady] = useState(false);
  const [isGooglePayReady, setIsGooglePayReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const btnWrapRef = useRef(null);
  const paymentsClientRef = useRef(null);

  useEffect(() => {
    const existingPayJs = document.querySelector(
      'script[src="https://pay.google.com/gp/p/js/pay.js"]',
    );
    if (existingPayJs) {
      if (window.google?.payments?.api?.PaymentsClient) {
        setIsGooglePayReady(true);
      }
    } else {
      const googlePayScript = document.createElement("script");
      googlePayScript.src = "https://pay.google.com/gp/p/js/pay.js";
      googlePayScript.async = true;
      googlePayScript.onload = () => setIsGooglePayReady(true);
      googlePayScript.onerror = () => {
        console.error("[GooglePay] Failed to load pay.js");
        alert("Google Pay SDK failed to load");
      };
      document.head.appendChild(googlePayScript);
    }

    const existingMoneris = document.querySelector(
      'script[src="https://www3.moneris.com/googlepay/googlepay-api.js"]',
    );
    if (!existingMoneris) {
      const monerisScript = document.createElement("script");
      monerisScript.src = "https://www3.moneris.com/googlepay/googlepay-api.js";
      monerisScript.async = true;
      monerisScript.onload = () => setupMonerisGooglePay();
      monerisScript.onerror = () => {
        const fallbackScript = document.createElement("script");
        fallbackScript.src =
          "https://gateway.moneris.com/googlepay/googlepay-api.js";
        fallbackScript.async = true;
        fallbackScript.onload = () => setupMonerisGooglePay();
        fallbackScript.onerror = () => {
          console.error(
            "[GooglePay] Failed to load moneris googlepay-api.js (fallback)",
          );
          alert("Moneris Google Pay script failed to load");
        };
        document.head.appendChild(fallbackScript);
      };
      document.head.appendChild(monerisScript);
    } else {
      if (window.MonerisGooglePay) {
        setIsMonerisReady(true);
      }
    }
  }, []);

  const setupMonerisGooglePay = () => {
    if (!document.getElementById("moneris-google-pay")) {
      const elem = document.createElement("div");
      elem.style.cssText = "display:none";
      elem.id = "moneris-google-pay";
      elem.setAttribute("store-id", "gwca058352");
      elem.setAttribute(
        "web-merchant-key",
        "873985850344E8A8E6416E51C9FE5A46228D48C41580EDCE52E9B5403C90A774",
      );
      elem.setAttribute("environment", "TEST");
      elem.setAttribute("country-code", "CA");
      document.body.appendChild(elem);
    }

    const tryInitialize = (attempt = 1) => {
      if (window.MonerisGooglePay) {
        try {
          window.MonerisGooglePay.onReady();
          setIsMonerisReady(true);
        } catch (err) {
          console.error(
            "[GooglePay] Error calling MonerisGooglePay.onReady():",
            err,
          );
        }
      } else if (attempt < 8) {
        setTimeout(() => tryInitialize(attempt + 1), 300);
      } else {
        console.error(
          "[GooglePay] MonerisGooglePay not available after 8 attempts",
        );
        alert("Moneris Google Pay failed to initialize");
      }
    };

    tryInitialize();
  };

  const handleGooglePayClick = useCallback(async () => {
    console.log("[GooglePay] Button clicked", {
      amount,
      paymentGuid,
      hasGoogle: !!window.google,
      hasMoneris: !!window.MonerisGooglePay,
    });
    if (
      isProcessing ||
      disabled ||
      !window.google ||
      !window.MonerisGooglePay ||
      !amount
    ) {
      return;
    }

    setIsProcessing(true);

    try {
      const paymentsClient =
        paymentsClientRef.current ||
        new window.google.payments.api.PaymentsClient({
          environment: "TEST",
        });

      paymentsClientRef.current = paymentsClient;

      const formattedAmount = parseFloat(amount).toFixed(2);

      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            type: "CARD",
            parameters: {
              allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
              allowedCardNetworks: ["MASTERCARD", "VISA", "AMEX", "DISCOVER"],
            },
            tokenizationSpecification: {
              type: "PAYMENT_GATEWAY",
              parameters: {
                gateway: "moneris",
                gatewayMerchantId: "gwca058352",
              },
            },
          },
        ],
        merchantInfo: {
          merchantName: "PHONEBOX NETWORK LTD",
          merchantId: "BCR2DN4T7DXMZ7CB",
        },
        transactionInfo: {
          countryCode: "CA",
          currencyCode: "USD",
          totalPriceStatus: "FINAL",
          totalPrice: formattedAmount,
        },
      };

      const paymentData =
        await paymentsClient.loadPaymentData(paymentDataRequest);
      console.log("[GooglePay] paymentData raw:", paymentData);
      console.log(
        "[GooglePay] Payment Data from Google:",
        JSON.stringify(paymentData, null, 2),
      );

      const timestamp = new Date().toISOString().replace(/[-:T.]/g, "").slice(0, 14);
      const uniqueOrderId = `${paymentGuid}_${timestamp}`;
      
      paymentData.orderId = uniqueOrderId;
      paymentData.amount = formattedAmount;

      console.log("[GooglePay] Sending to Moneris with orderId:", uniqueOrderId, "amount:", formattedAmount);
           
      const googleCardInfo = paymentData?.paymentMethodData?.info || {};

      window.MonerisGooglePay.purchase(paymentData, (response) => {
        console.log("[GooglePay] Moneris raw response:", response);
        console.log(
          "[GooglePay] Moneris Response:",
          JSON.stringify(response, null, 2),
        );
        
        response.googleCardInfo = {
          cardDetails: googleCardInfo.cardDetails || "",       // last 4 digits
          cardNetwork: googleCardInfo.cardNetwork || "",       // VISA, MASTERCARD, etc.
          billingAddress: googleCardInfo.billingAddress || {},
        };

        const code = response?.receipt?.ResponseCode;

        if (code != null && !isNaN(code)) {
          const responseCode = parseInt(code, 10);

          if (responseCode < 50) {
            onPaymentSuccess?.(response);
            return;
          }
          console.error("[GooglePay] Payment declined:", response);
          onPaymentError?.({
            message:
              response?.receipt?.Message ||
              "Payment declined by your bank or card company.",
          });
          setIsProcessing(false);
          return;
        }

        onPaymentError?.({
          message: "Error processing payment. Please try again.",
        });
        setIsProcessing(false);
      });
    } catch (error) {
      // console.error("[GooglePay] Error:", error);
      console.error("[GooglePay] Error:", error);
      console.error("[GooglePay] Error JSON:", JSON.stringify(error, null, 2));
      console.error("[GooglePay] statusCode:", error?.statusCode);
      console.error("[GooglePay] statusMessage:", error?.statusMessage);
      console.error("[GooglePay] message:", error?.message);

      // if (error?.statusCode !== "CANCELED") {
      //   onPaymentError?.({
      //     message: error?.statusMessage || error?.message || "Payment failed",
      //   });
      // }
      onPaymentError?.({
        message: error?.statusMessage || error?.message || "Payment failed",
      });
      setIsProcessing(false);
    }
  }, [
    isProcessing,
    disabled,
    amount,
    paymentGuid,
    onPaymentSuccess,
    onPaymentError,
  ]);

  useEffect(() => {
    if (!isMonerisReady || !isGooglePayReady) return;
    if (!btnWrapRef.current) return;

    let cancelled = false;

    const tryCreate = (attempt = 1) => {
      if (cancelled) return;

      const PaymentsClient = window.google?.payments?.api?.PaymentsClient;
      if (!PaymentsClient) {
        if (attempt < 10) {
          return setTimeout(() => tryCreate(attempt + 1), 200);
        }
        console.error("[GooglePay] PaymentsClient not available after retries");
        return;
      }

      const paymentsClient =
        paymentsClientRef.current ||
        new PaymentsClient({ environment: "TEST" });
      paymentsClientRef.current = paymentsClient;

      btnWrapRef.current.innerHTML = "";

      const button = paymentsClient.createButton({
        onClick: handleGooglePayClick,
        buttonType: "pay",
        buttonColor: "black",
        buttonSizeMode: "fill",
      });

      btnWrapRef.current.appendChild(button);
    };

    tryCreate();

    return () => {
      cancelled = true;
    };
  }, [isMonerisReady, isGooglePayReady, handleGooglePayClick]);

  if (!isMonerisReady) return null;

  return (
    <GooglePayButtonContainer>
      <GooglePayButtonWrap
        ref={btnWrapRef}
        aria-disabled={disabled || isProcessing}
      />
      {isProcessing && <Hint>Processing...</Hint>}
    </GooglePayButtonContainer>
  );
};

const GooglePayButtonContainer = styled.div`
  margin: 20px 0;
`;

const GooglePayButtonWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  & > * {
    width: 100%;
  }
`;

const Hint = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #666;
  text-align: center;
`;

export default GooglePayButton;
